import { StyledButton } from '@/pages/PreConsultationForms/styled';
import type { BasicErrorResponseSchema } from '@montugroup/apis-styleguide';
import { Box, Link, Stack, Typography } from '@montugroup/design-system';
import type { HandleStepChange } from '@montugroup/form-builder';
import type { FormSubmissionResponse } from '@montugroup/form-builder/hooks/useFormSubmission';
import { ALT_CUSTOMER_SERVICE_NUMBER_DISPLAY, ALT_CUSTOMER_SERVICE_NUMBER_INTL } from '@montugroup/frontend-common';
import { Logger } from '@montugroup/utilities';
import { styled } from '@mui/material';
import React from 'react';

const logger = new Logger('FormErrorMessage');

const GENERIC_ERROR_TEXT = 'There was an error while submitting the form.';

const Image = styled('img')(({ theme: t }) => ({
  maxWidth: '26.5rem',
  [t.breakpoints.down('md')]: {
    maxWidth: '21.5rem',
    margin: '0 auto',
  },
}));

const GetErrorImage = (code: number) => {
  switch (code) {
    case 404:
      return <Image src={'/assets/error404.svg'} alt="error-image" aria-hidden="true" />;
    case 500:
      return <Image src={'/assets/error500.svg'} alt="error-image" aria-hidden="true" />;
    default:
      return <Image src={'/assets/errorGeneric.svg'} alt="error-image" aria-hidden="true" />;
  }
};

const GetErrorDescription = (responseObject?: object) => {
  if (responseObject && Object.hasOwn(responseObject, 'error')) {
    const basicErrorSchema = responseObject as BasicErrorResponseSchema;
    return basicErrorSchema.error.message;
  }

  return GENERIC_ERROR_TEXT;
};

/**
 * The step a patient is sent back to for an error they can fix themselves,
 * keyed by the message the API returns. Step indexes differ per form, so each
 * form supplies its own.
 */
export type ReturnStepsByError = Record<string, number>;

const GetReturnStepFromError = (errorText: string, returnSteps: ReturnStepsByError) =>
  returnSteps[errorText.toLowerCase().trim()] ?? null;

const GetErrorTitle = (code: number, _errorText: string) => {
  if (code === 400) {
    return "There is an error with the information you've entered";
  }

  return "We're sorry, an error has occurred";
};

const GetContactText = (errorText: string) => {
  if (errorText === GENERIC_ERROR_TEXT) {
    return 'Please contact us at ';
  }

  return 'If you need assistance, please contact us at ';
};

type ErrorPageDetails = {
  errorTitle: string;
  errorText: string;
  errorImage: React.ReactElement;
  contactText: string;
  responseStatus: number;
  returnStep: number | null;
};

const GetErrorPageDetails = (
  response: FormSubmissionResponse | undefined,
  returnSteps: ReturnStepsByError,
): ErrorPageDetails => {
  const responseStatus = response?.status || 500;
  const responseObject = response?.body ?? undefined;

  const errorText = GetErrorDescription(responseObject);
  const returnStep = GetReturnStepFromError(errorText, returnSteps);
  const errorTitle = GetErrorTitle(responseStatus, errorText);
  const contactText = GetContactText(errorText);
  const errorImage = GetErrorImage(responseStatus);

  logger.error(`Form submission error`, {
    errorResponse: responseObject,
  });

  return { errorTitle, errorText, errorImage, contactText, responseStatus, returnStep };
};

/**
 * Builds the error slot a form hands to the FormBuilder, so a failed
 * submission offers a way back rather than a dead end.
 */
export const createFormErrorMessage = (returnSteps: ReturnStepsByError) =>
  function FormErrorMessage(handleStepChange?: HandleStepChange, response?: FormSubmissionResponse) {
    const { errorTitle, errorText, errorImage, returnStep, contactText } = GetErrorPageDetails(response, returnSteps);
    const showStepReturn = returnStep && handleStepChange;

    return (
      <Stack sx={{ alignItems: 'center', textAlign: showStepReturn ? 'left' : 'center' }}>
        {!showStepReturn && errorImage}

        <Box sx={{ flexGrow: { xs: 1, md: 'auto' } }}>
          <Typography variant="h5" component="h1" sx={{ color: 'primary.main', fontWeight: 500, mt: '1rem' }}>
            {errorTitle}
          </Typography>

          <Typography sx={{ mt: '1rem' }} variant="h6" paragraph>
            {errorText}
          </Typography>

          {showStepReturn && (
            <Box sx={{ mt: 10, mb: 10 }}>
              <StyledButton size="large" variant="contained" onClick={() => handleStepChange(returnStep)}>
                Return to form
              </StyledButton>
            </Box>
          )}

          <Typography sx={{ mt: '1rem' }} variant={showStepReturn ? 'body1' : 'h6'} paragraph>
            {contactText}{' '}
            <Link href={`tel:${ALT_CUSTOMER_SERVICE_NUMBER_INTL}`}>{ALT_CUSTOMER_SERVICE_NUMBER_DISPLAY}</Link>
          </Typography>
        </Box>
      </Stack>
    );
  };
